/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
// Internal helpers shared across composer/core/* modules. Not part of the public composerCore API.
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type {
  ComposerSnapshot,
  ContentReferenceType,
  ContentReferenceOption,
  ContentReferenceGroup,
} from "./types";

export const briefDraftsTable = () => (supabase as any).from("content_piece_drafts");
export const briefPublicationsTable = () => (supabase as any).from("content_piece_publications");

export const normalizeText = (value: string) => value.trim().replace(/\s+/g, " ");

export const referenceKey = (referenceType: ContentReferenceType, referenceId: string) =>
  `${referenceType}:${referenceId}`;

export const asStringArray = (value: Json | null | undefined): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

export const asObjectArray = <T>(value: Json | null | undefined): T[] =>
  Array.isArray(value)
    ? value.filter((item) => typeof item === "object" && item !== null && !Array.isArray(item)).map((item) => item as T)
    : [];

export const getFriendlyMessage = (error: { code?: string; message?: string } | null, fallback: string) => {
  if (error?.code === "23505") return "That name already exists in this workspace.";
  return error?.message ?? fallback;
};

export const referenceGroupOrder: ContentReferenceType[] = [
  "voice_profile", "avatar", "column", "principle", "topic", "source", "brief", "readwise_highlight", "excerpt",
];

export const referenceTypeLabels: Record<ContentReferenceType, string> = {
  voice_profile: "Voice",
  avatar: "Avatars",
  column: "Framework columns",
  principle: "Principles",
  topic: "Topics",
  source: "Sources",
  brief: "Briefs",
  readwise_highlight: "Readwise highlights",
  excerpt: "My Excerpts",
};

const createReferenceToken = (label: string) => `@{${label}}`;

export const buildReferenceLookup = (snapshot: ComposerSnapshot) => {
  const columnLookup = new Map(snapshot.columns.map((column) => [column.id, column]));
  const topicLookup = new Map(snapshot.topics.map((topic) => [topic.id, topic]));

  const optionMap = new Map<string, ContentReferenceOption>();

  if (snapshot.voiceProfile) {
    optionMap.set(referenceKey("voice_profile", snapshot.voiceProfile.id), {
      id: snapshot.voiceProfile.id,
      referenceType: "voice_profile",
      label: snapshot.voiceProfile.title,
      description: snapshot.voiceProfile.core_identity,
      groupKey: "voice_profile",
      groupLabel: referenceTypeLabels.voice_profile,
      colorKey: "indigo",
      token: createReferenceToken(snapshot.voiceProfile.title),
    });
  }

  snapshot.avatars.forEach((avatar) => {
    optionMap.set(referenceKey("avatar", avatar.id), {
      id: avatar.id,
      referenceType: "avatar",
      label: avatar.name,
      description: avatar.summary,
      groupKey: "avatar",
      groupLabel: referenceTypeLabels.avatar,
      colorKey: "sage",
      token: createReferenceToken(avatar.name),
    });
  });

  snapshot.columns.forEach((column) => {
    optionMap.set(referenceKey("column", column.id), {
      id: column.id,
      referenceType: "column",
      label: column.label,
      description: column.progression_key_label
        ? `${column.progression_key_label}${column.description ? ` · ${column.description}` : ""}`
        : column.description,
      groupKey: "column",
      groupLabel: referenceTypeLabels.column,
      colorKey: column.color_key,
      token: createReferenceToken(column.label),
    });
  });

  snapshot.principles.forEach((principle) => {
    const column = columnLookup.get(principle.framework_column_id);
    optionMap.set(referenceKey("principle", principle.id), {
      id: principle.id,
      referenceType: "principle",
      label: principle.label,
      description: column ? `Within ${column.label}` : principle.description,
      groupKey: "principle",
      groupLabel: referenceTypeLabels.principle,
      colorKey: column?.color_key ?? "amber",
      token: createReferenceToken(principle.label),
    });
  });

  snapshot.topics.forEach((topic) => {
    const column = columnLookup.get(topic.framework_column_id);
    const parentTopic = topic.parent_topic_id ? topicLookup.get(topic.parent_topic_id) : null;
    optionMap.set(referenceKey("topic", topic.id), {
      id: topic.id,
      referenceType: "topic",
      label: topic.label,
      description: parentTopic
        ? `Subtopic of ${parentTopic.label}`
        : column
          ? `Within ${column.label}`
          : topic.description,
      groupKey: "topic",
      groupLabel: referenceTypeLabels.topic,
      colorKey: column?.color_key ?? "sky",
      token: createReferenceToken(topic.label),
    });
  });

  snapshot.sources.forEach((source) => {
    optionMap.set(referenceKey("source", source.id), {
      id: source.id,
      referenceType: "source",
      label: source.title,
      description: `${source.bucket[0].toUpperCase()}${source.bucket.slice(1)}${source.source_type ? ` · ${source.source_type}` : ""}`,
      groupKey: "source",
      groupLabel: referenceTypeLabels.source,
      colorKey:
        source.bucket === "primary" ? "plum" : source.bucket === "secondary" ? "amber" : "silver",
      token: createReferenceToken(source.title),
    });
  });

  snapshot.briefs.forEach((brief) => {
    optionMap.set(referenceKey("brief", brief.id), {
      id: brief.id,
      referenceType: "brief",
      label: brief.title,
      description: brief.content_type,
      groupKey: "brief",
      groupLabel: referenceTypeLabels.brief,
      colorKey: "coral",
      token: createReferenceToken(brief.title),
    });
  });

  snapshot.readwiseHighlights.forEach((highlight) => {
    const truncated = highlight.highlight_text.length > 80
      ? `${highlight.highlight_text.slice(0, 80).trim()}…`
      : highlight.highlight_text;
    const label = highlight.book_title
      ? `${highlight.book_title}: ${truncated}`
      : truncated;
    optionMap.set(referenceKey("readwise_highlight", highlight.id), {
      id: highlight.id,
      referenceType: "readwise_highlight",
      label,
      description: highlight.book_author
        ? `${highlight.book_author}${highlight.note ? ` · note: ${highlight.note.slice(0, 80)}` : ""}`
        : highlight.note ?? null,
      groupKey: "readwise_highlight",
      groupLabel: referenceTypeLabels.readwise_highlight,
      colorKey: "amber",
      token: createReferenceToken(highlight.book_title ? `${highlight.book_title} highlight` : "highlight"),
    });
  });

  snapshot.excerpts.forEach((excerpt) => {
    const truncated = excerpt.body_text.length > 80
      ? `${excerpt.body_text.slice(0, 80).trim()}…`
      : excerpt.body_text;
    const sourceLabel = excerpt.publication_title || excerpt.library_title || excerpt.author_name;
    const label = sourceLabel ? `${sourceLabel}: ${truncated}` : truncated;
    const descriptionParts = [
      excerpt.author_name,
      excerpt.notes ? `note: ${excerpt.notes.slice(0, 80)}` : null,
    ].filter(Boolean) as string[];
    optionMap.set(referenceKey("excerpt", excerpt.id), {
      id: excerpt.id,
      referenceType: "excerpt",
      label,
      description: descriptionParts.length ? descriptionParts.join(" · ") : null,
      groupKey: "excerpt",
      groupLabel: referenceTypeLabels.excerpt,
      colorKey: "plum",
      token: createReferenceToken(sourceLabel ? `${sourceLabel} excerpt` : "excerpt"),
    });
  });
  return optionMap;
};

export const buildContentReferenceGroups = (snapshot: ComposerSnapshot): ContentReferenceGroup[] => {
  const grouped = new Map<ContentReferenceType, ContentReferenceOption[]>();
  const optionMap = buildReferenceLookup(snapshot);

  Array.from(optionMap.values()).forEach((option) => {
    const current = grouped.get(option.groupKey) ?? [];
    current.push(option);
    grouped.set(option.groupKey, current);
  });

  return referenceGroupOrder
    .map((key) => ({
      key,
      label: referenceTypeLabels[key],
      options: (grouped.get(key) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .filter((group) => group.options.length > 0);
};

export const invokeConnectorManage = async (workspaceId: string, payload: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke("content-connector-manage", {
    body: { workspace_id: workspaceId, ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};
